import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
} from '@mui/material';

/**
 * VolumePivotTable
 *
 * Renders a request_type × year cross-tab:
 *
 *   Row Labels        | 2023 | 2024 | 2025 | Grand Total
 *   Hard WAN Upgrade  |    8 |    2 |    3 |          13
 *   ...
 *   Grand Total       |  226 |  161 |  195 |         582
 *
 * In 'ratio' mode values are shown as percentages of each year's column total.
 *
 * Props:
 *   data        — rawGraph2 flat array:
 *                 [{ request_type, year, new_count, wip_count, completed_count, total }, ...]
 *   mode        — 'volume' | 'ratio'  (default 'volume')
 *   statusKeys  — which count fields to sum (default all three)
 *   accentColor — header colour
 */
const VolumePivotTable = ({
  data = [],
  mode = 'volume',
  statusKeys = ['new_count', 'wip_count', 'completed_count'],
  accentColor = '#003781',
}) => {
  if (!data || data.length === 0) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
        No data available
      </Typography>
    );
  }

  // Collect ordered unique values
  const years = [...new Set(data.map((r) => r.year))].sort();
  const types = [...new Set(data.map((r) => r.request_type))].sort();

  // Sum the requested status fields for a single raw row
  const rowValue = (row) =>
    statusKeys.reduce((s, k) => s + (row[k] || 0), 0);

  // Build lookup: { request_type: { year: summedValue } }
  const lookup = {};
  data.forEach((row) => {
    if (!lookup[row.request_type]) lookup[row.request_type] = {};
    lookup[row.request_type][row.year] =
      (lookup[row.request_type][row.year] || 0) + rowValue(row);
  });

  // Column totals (per year)
  const colTotal = (year) =>
    types.reduce((s, t) => s + (lookup[t]?.[year] || 0), 0);

  // Row totals (per type across all years)
  const rowTotal = (type) =>
    years.reduce((s, y) => s + (lookup[type]?.[y] || 0), 0);

  // Grand total
  const grandTotal = types.reduce((s, t) => s + rowTotal(t), 0);

  // Format a cell value based on mode
  const fmt = (value, yearColTotal) => {
    if (mode === 'ratio') {
      if (!yearColTotal) return '—';
      return `${Math.round((value / yearColTotal) * 100)}%`;
    }
    return value || '—';
  };

  // Grand total cell value
  const fmtGrandCell = (value, total) => {
    if (mode === 'ratio') {
      if (!total) return '—';
      return `${Math.round((value / total) * 100)}%`;
    }
    return value || '—';
  };

  // Shared styles
  const headerSx = {
    fontWeight: 700,
    fontSize: 12,
    color: '#fff',
    bgcolor: accentColor,
    borderColor: `${accentColor}80`,
    py: 1.25,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  };
  const labelHeaderSx = { ...headerSx, textAlign: 'left', minWidth: 200 };
  const bodySx = {
    fontSize: 13,
    color: '#374151',
    py: 1,
    textAlign: 'right',
    borderColor: '#f0f1f3',
  };
  const labelSx = { ...bodySx, textAlign: 'left', fontWeight: 500 };
  const grandRowSx = {
    fontWeight: 700,
    fontSize: 13,
    color: '#1a1a2e',
    bgcolor: '#dce3f0',
    borderTop: `2px solid ${accentColor}50`,
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={labelHeaderSx}>Row Labels</TableCell>
            {years.map((y) => (
              <TableCell key={y} sx={headerSx}>{y}</TableCell>
            ))}
            <TableCell sx={headerSx}>Grand Total</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {types.map((type, i) => (
            <TableRow
              key={type}
              sx={{
                bgcolor: i % 2 === 0 ? '#fff' : '#fafbff',
                '&:hover': { bgcolor: `${accentColor}08` },
              }}
            >
              <TableCell sx={labelSx}>{type}</TableCell>
              {years.map((y) => (
                <TableCell key={y} sx={bodySx}>
                  {fmt(lookup[type]?.[y] || 0, colTotal(y))}
                </TableCell>
              ))}
              <TableCell sx={{ ...bodySx, fontWeight: 600 }}>
                {fmtGrandCell(rowTotal(type), grandTotal)}
              </TableCell>
            </TableRow>
          ))}

          {/* Grand total row */}
          <TableRow>
            <TableCell sx={{ ...grandRowSx, py: 1, textAlign: 'left' }}>
              Grand Total
            </TableCell>
            {years.map((y) => (
              <TableCell key={y} sx={{ ...grandRowSx, py: 1, textAlign: 'right' }}>
                {mode === 'ratio' ? '100%' : colTotal(y)}
              </TableCell>
            ))}
            <TableCell sx={{ ...grandRowSx, py: 1, textAlign: 'right' }}>
              {mode === 'ratio' ? '100%' : grandTotal}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default VolumePivotTable;
